import React, { useState, useEffect } from 'react';
import styles from './DecimalInput.module.scss';

export interface DecimalInputProps {
    id?: string;
    name: string;
    value?: number | null;
    onChange: (event: { target: { name: string; value: number | null } }) => void;
    label?: string;
    placeholder?: string;
    className?: string;
    labelClassName?: string;
    disabled?: boolean;
    required?: boolean;
    min?: number;
    max?: number;
    step?: number;
    error?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const DecimalInput: React.FC<DecimalInputProps> = ({
    id,
    name,
    value,
    onChange,
    label,
    placeholder,
    className = '',
    labelClassName = '',
    disabled = false,
    required = false,
    min,
    max,
    step = 0.01,
    error,
    onBlur,
    onFocus,
}) => {
    const [inputValue, setInputValue] = useState(value?.toString() || '');

    // Update local state when value prop changes
    useEffect(() => {
        setInputValue(value?.toString() || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Parse to number and call onChange with the parsed value
        const numericValue = newValue === '' ? null : parseFloat(newValue);
        
        // Validate the parsed value
        if (newValue === '' || (!isNaN(numericValue as number) && isValidNumber(numericValue as number))) {
            onChange({
                target: {
                    name,
                    value: numericValue
                }
            });
        }
    };

    const isValidNumber = (num: number): boolean => {
        if (min !== undefined && num < min) return false;
        if (max !== undefined && num > max) return false;
        return true;
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Format the value on blur if it's a valid number
        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            setInputValue(numericValue.toString());
        }
        onBlur?.(e);
    };

    return (
        <>
            {label && (
                <label 
                    className={`${styles.label} ${labelClassName}`} 
                    htmlFor={id || name}
                >
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            <input
                type="text"
                inputMode="decimal"
                id={id || name}
                name={name}
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={onFocus}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                min={min}
                max={max}
                step={step}
                autoComplete='off'
                spellCheck="false"
                className={`${styles.input} ${error ? styles.error : ''} ${className}`}
            />
            {error && <span className={styles.errorMessage}>{error}</span>}
        </>
    );
}; 